import {
  LineChart, Line, XAxis, YAxis,
  Tooltip, ResponsiveContainer, CartesianGrid,
} from 'recharts';
import { TrendingUp } from 'lucide-react';
import type { ThroughputPoint } from '@/hooks/useTeamMonitoring';

// ─── Types ──────────────────────────────────────────────────────────────────

interface ReviewThroughputChartProps {
  data: ThroughputPoint[];
  loading?: boolean;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

/** Format "2026-04-07" → "Apr 7" */
function shortDate(iso: string): string {
  const d = new Date(iso + 'T00:00:00');
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

// ─── Skeleton ───────────────────────────────────────────────────────────────

function ChartSkeleton() {
  return (
    <div role="status" aria-label="Loading throughput chart" className="h-[200px] animate-pulse rounded bg-muted" aria-hidden="true" />
  );
}

// ─── Component ───────────────────────────────────────────────────────────────

/**
 * Review Throughput line chart — FR-083.
 * Shows daily completed reviews over the last 30 days.
 * Admin-only (rendered inside admin-gated TeamMonitoringTab).
 *
 * WCAG: sr-only description lists key data points for screen readers.
 */
export function ReviewThroughputChart({ data, loading }: ReviewThroughputChartProps) {
  const labelledData = data.map((d) => ({ ...d, label: shortDate(d.date) }));

  return (
    <section
      className="rounded-lg border border-border bg-card p-4 space-y-3"
      aria-label="Review throughput over last 30 days"
    >
      <div className="flex items-center gap-2">
        <TrendingUp className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
        <h3 className="text-sm font-semibold text-foreground">Throughput (last 30 days)</h3>
      </div>

      {loading ? (
        <ChartSkeleton />
      ) : data.length === 0 ? (
        <p className="text-xs text-muted-foreground italic py-8 text-center">No throughput data yet.</p>
      ) : (
        <>
          <p className="sr-only">
            Line chart of daily completed reviews over the last 30 days.
            Peak: {Math.max(...data.map((d) => d.completed))} reviews in a single day.
          </p>
          <div className="h-[200px]" aria-hidden="true">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={labelledData} margin={{ left: 0, right: 8, top: 4, bottom: 4 }}>
                <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                <XAxis
                  dataKey="label"
                  tick={{ fontSize: 10 }}
                  tickLine={false}
                  axisLine={false}
                  interval="preserveStartEnd"
                />
                <YAxis
                  allowDecimals={false}
                  tick={{ fontSize: 11 }}
                  tickLine={false}
                  axisLine={false}
                  width={28}
                />
                <Tooltip
                  formatter={(value) => [value, 'Reviews completed']}
                  contentStyle={{
                    backgroundColor: 'hsl(var(--card))',
                    border: '1px solid hsl(var(--border))',
                    borderRadius: '6px',
                    fontSize: 12,
                  }}
                />
                <Line
                  type="monotone"
                  dataKey="completed"
                  stroke="hsl(var(--primary))"
                  strokeWidth={2}
                  dot={false}
                  activeDot={{ r: 4 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </>
      )}
    </section>
  );
}
